import { Users } from "@prisma/client"
import Typography from "~/components/typography"
import { Link, useFetcher, useRouteLoaderData } from "@remix-run/react"
import { Avatar, AvatarImage, AvatarFallback } from "~/components/ui/avatar"
import { useEffect, useState } from "react"
import showdown from "showdown"
import moment from "moment"
import { FormText, SubmitButton } from "~/components/form-fields"
import { ValidatedForm } from "remix-validated-form"
import { Button } from "~/components/ui/button"
import { Pencil2Icon, ResetIcon, TrashIcon } from "@radix-ui/react-icons"
import { ConfirmDialog } from "~/components/dialogs"
import { cn } from "~/utils/shadecn"
import { avatarImageClassName } from "~/utils/visibility"
import { z } from "zod"
import { withZod } from "@remix-validated-form/with-zod"

export const commentSchema = z.object({
  VideoID: z.coerce.number(),
  ParentCommentID: z.coerce.number().optional(),
  Content: z.string().min(1, { message: "cannot be empty" }),
})
export const commentValidator = withZod(commentSchema)
const deleteSchema = z.object({
  CommentID: z.coerce.number(),
})
export const deleteValidator = withZod(deleteSchema)
const updateSchema = z.object({
  CommentID: z.coerce.number(),
  Content: z.string().min(1, { message: "cannot be empty" }),
})
export const updateValidator = withZod(updateSchema)

export type CommentAndUser = {
  CommentID: bigint
  ParentCommentID: bigint | null
  UserID: bigint | null
  Content: string
  Status: number
  Deleted: number
  UserPrivate: number
  UserName: string
  DisplayName: string
  Avatar: string
  CreatedAt: Date
  IsAnonymous: boolean
  PurchaseID: number | null
  IsLoveNote: boolean
  HasLoveObject: boolean
  LoveObjectSrc: string | null
  IsSFW?: boolean
}
export type CommentAndChildren = CommentAndUser & {
  Children: CommentAndChildren[]
}

export function Comment({
  videoID,
  currentUser,
  currentUserAvatar,
  level,
  comment,
  disabled,
  fetcher,
}: {
  videoID: number
  currentUser: Users
  currentUserAvatar: string
  level: number
  comment: CommentAndChildren
  disabled?: boolean
  fetcher: ReturnType<typeof useFetcher>
}) {
  const rootData: any = useRouteLoaderData("root")
  const converter = new showdown.Converter()
  const [replying, setReplying] = useState(false)
  const [edit, setEdit] = useState(false)
  const [commentToDelete, setCommentToDelete] = useState<number | null>(null)
  const [confirmDelete, setConfirmDelete] = useState(false)

  function openReply(e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) {
    e.preventDefault()
    setReplying(true)
  }
  function openEdit(e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) {
    e.preventDefault()
    setEdit(true)
  }
  function openConfirm(e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) {
    e.preventDefault()
    setConfirmDelete(true)
  }

  function handleDelete() {
    setConfirmDelete(false)
    const formData = new FormData()
    formData.append("CommentID", commentToDelete!.toString())
    fetcher.submit(formData, {
      action: `/videos/${videoID}/comments`,
      method: "delete",
    })
  }

  useEffect(() => {
    if (!fetcher.formData) {
      setReplying(false)
      setEdit(false)
      setCommentToDelete(null)
    }
  }, [fetcher.formData])

  const commentBoxClass = cn("group hover:bg-primary/5 p-1 rounded-md transition-colors duration-400", {
    "relative bg-background-special border border-primary text-center py-2 rounded-md": comment.IsLoveNote,
    "": !comment.IsLoveNote,
  })

  return (
    <div className="mt-3">
      <div className={commentBoxClass}>
        <div className="flex justify-between items-center gap-2">
          {comment.IsLoveNote && (
            <div className="absolute h-7 top-0 right-0 transform bg-primary rounded-md">
              <span className="font-medium text-xs text-center text-primary-foreground m-2">Love Note</span>
            </div>
          )}
          <div className="flex items-center gap-2">
            <Avatar className={cn("h-5 w-5", level > 0 && "h-4 w-4")}>
              <AvatarImage
                src={comment.Avatar}
                className={avatarImageClassName(rootData?.loggedIn, rootData?.isPaymentVerified, comment.IsSFW)}
                alt="avatar"
              />
              <AvatarFallback>JA</AvatarFallback>
            </Avatar>
            <Typography>
              <h4
                className={cn(
                  "font-medium text-[12px] sm:text-[14px] tracking-wide",
                  level > 0 && "text-[10px] sm:text-[12px]"
                )}>
                {!disabled && comment.UserName && comment.UserPrivate !== 1 && !comment.IsAnonymous ?
                  <Link to={`/makelovenotpornstars/${comment.UserName}`}>{comment.DisplayName}</Link>
                : <span>{comment.IsAnonymous ? "Secret Admirer" : comment.DisplayName}</span>}
              </h4>
            </Typography>
            <p className="text-muted-foreground text-[9px] sm:text-xs">
              {moment(comment.CreatedAt!.toString()).fromNow()}
            </p>
          </div>
          <div className="mr-20 flex items-center gap-2">
            {!disabled && !comment.Deleted && !replying && (
              <Typography className="flex gap-2 prose-sm text-xs opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                {level < 3 && (
                  <Link to="#" className="flex items-center gap-1 no-underline hover:underline" onClick={openReply}>
                    <ResetIcon className="h-3 w-3" />
                    <span>reply</span>
                  </Link>
                )}
                {currentUser?.UserID === comment.UserID && (
                  <>
                    {comment.Status == 0 && (
                      <Link to="#" className="flex items-center gap-1 no-underline hover:underline" onClick={openEdit}>
                        <Pencil2Icon className="h-3 w-3" />
                        <span className="hidden sm:inline">edit</span>
                      </Link>
                    )}
                    <Link
                      to="#"
                      onClick={(e) => {
                        setCommentToDelete(Number(comment.CommentID))
                        openConfirm(e)
                      }}
                      className="flex items-center gap-1 no-underline hover:underline">
                      <TrashIcon className="h-3 w-3" />
                      <span className="hidden sm:inline">delete</span>
                    </Link>
                  </>
                )}
              </Typography>
            )}
          </div>
        </div>

        {comment.HasLoveObject && comment.LoveObjectSrc && (
          <div className="flex justify-center sm:mx-6">
            <div className="relative">
              <img src={comment.LoveObjectSrc} alt="Love Object" className="w-24 h-24 sm:w-32 sm:h-32" loading="lazy"/>
            </div>
          </div>
        )}

        {edit ?
          <ValidatedForm
            className="mx-6 space-y-2 mb-2"
            validator={updateValidator}
            action={`/videos/${videoID}/comments`}
            fetcher={fetcher}
            method="put"
            defaultValues={{
              Content: comment.Content!,
            }}>
            <input type="hidden" name="CommentID" value={Number(comment.CommentID)} />
            <FormText
              name="Content"
              placeholder="Leave a reply"
              autoFocus={true}
              inputClasses="min-h-12 h-12 bg-background text-xs px-3"
            />
            <SubmitButton size="sm" className="w-32" content="Update" loading="Updating..." />
            <Button
              className="ml-2 w-32 bg-background"
              size="sm"
              variant="outline"
              onClick={(e) => {
                e.preventDefault()
                setEdit(false)
              }}>
              Cancel
            </Button>
          </ValidatedForm>
        : <div className="mx-6">
            {comment.Status === 0 && comment.Deleted === 0 && (
              <Typography className="inline-block prose-sm py-0.5 px-1 my-2 mr-4 text-xs bg-amber-200 dark:bg-yellow-500 text-foreground dark:text-background rounded-sm italic">
                Thanks for your comment! Hang tight while our curation team reviews it
              </Typography>
            )}
            <Typography
              className={cn("prose-sm first:prose-p:mt-1 last:prose-p:mb-1", {
                "text-primary": comment.IsLoveNote,
              })}>
              {comment.Deleted ?
                <p className="italic text-muted-foreground">This user has deleted their comment</p>
              : <div
                  dangerouslySetInnerHTML={{
                    __html: converter.makeHtml(comment.Content?.replace(/(?:\r\n|\r|\n)/g, "  \r\n") || ""),
                  }}
                />
              }
            </Typography>
          </div>
        }
      </div>

      {replying && (
        <div className="ml-6 group hover:bg-primary/5 p-1 rounded-md transition-colors duration-400">
          <div className="flex justify-start items-center gap-2">
            <Avatar className="h-4 w-4">
              <AvatarImage src={currentUserAvatar} className="object-cover" alt="avatar" />
              <AvatarFallback>JA</AvatarFallback>
            </Avatar>
            <Typography>
              <h4 className="text-md tracking-wide font-medium">{currentUser.DisplayName}</h4>
            </Typography>
            <p className="text-muted-foreground text-xs">{moment(new Date().toString()).fromNow()}</p>
          </div>

          <div className="ml-6 flex flex-col">
            <ValidatedForm
              className="mr-6 space-y-2 mb-2"
              validator={commentValidator}
              action={`/videos/${videoID}/comments`}
              fetcher={fetcher}
              method="post">
              <input type="hidden" name="VideoID" value={videoID} />
              <input type="hidden" name="ParentCommentID" value={Number(comment.CommentID)} />
              <FormText
                name="Content"
                placeholder="Leave a reply"
                autoFocus={true}
                inputClasses="min-h-[2.12rem] h-[2.12rem] bg-background text-xs px-3"
              />
              <SubmitButton size="sm" className="w-32" content="Reply" loading="Replying..." />
              <Button
                className="ml-2 w-32 bg-background"
                size="sm"
                variant="outline"
                onClick={() => setReplying(false)}>
                Cancel
              </Button>
            </ValidatedForm>
          </div>
        </div>
      )}

      {comment.Children.length > 0 && (
        <div className="ml-4 sm:ml-6">
          <div className="flex flex-col">
            {comment.Children.map((child, index) => (
              <Comment
                key={index}
                currentUser={currentUser}
                currentUserAvatar={currentUserAvatar}
                level={level + 1}
                videoID={videoID}
                comment={child}
                disabled={disabled}
                fetcher={fetcher}
              />
            ))}
          </div>
        </div>
      )}

      <ConfirmDialog
        open={confirmDelete}
        onOpenChange={setConfirmDelete}
        title="Delete Comment"
        content="Are you sure you want to delete this comment?"
        onConfirm={handleDelete}
      />
    </div>
  )
}
